import { Box, Image } from "@chakra-ui/react";
import {
  GifFragment,
  MessageFragment,
  MessageThreadFragment,
} from "gql/graphql";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useToggle } from "react-use";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  gif: Omit<GifFragment, "id">;
}

const MAX_PLAYBACK_SECONDS = 5 * 1000; // 5 seconds

const MessageGifViewer = ({ messageThread, message, gif }: IProps) => {
  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const [play, togglePlay] = useToggle(true);

  const src = useMemo(() => {
    if (!play) {
      return gif.imageUrl;
    }

    return gif.videoUrl ?? gif.imageUrl;
  }, [gif.imageUrl, gif.videoUrl, play]);

  const { width, height } = useMemo(() => {
    // fall back to a standard box when dimension metadata is missing so the
    // gif is never rendered into an invisible 0x0 box
    const width = gif.width || 320;
    const height = gif.height || 240;

    if (width > 320) {
      return {
        width: "320px",
        height: `${Math.round((320 / width) * height)}px`,
      };
    }

    return {
      width: `${width}px`,
      height: `${height}px`,
    };
  }, [gif.width, gif.height]);

useEffect(() => {
  const timer = setTimeout(() => {
    togglePlay(false);
  }, MAX_PLAYBACK_SECONDS);

  return () => clearTimeout(timer);
}, [message.isSeen, togglePlay]);

  const onClick = useCallback(() => togglePlay(), [togglePlay]);

  return (
    <Box pl={isViewingTranslatedMessage ? 3 : 0} pb={1}>
      <Box
        position="relative"
        width={width}
        height={height}
        borderRadius={4}
        overflow="hidden"
      >
        <Image
          src={src}
          alt={gif.name ?? ""}
          onClick={onClick}
          width="100%"
          height="100%"
          objectFit="cover"
          cursor="pointer"
        />
        <Image
          position="absolute"
          src="/klipy-watermark.svg"
          alt="Klipy"
          width="30px"
          height="auto"
          bottom={1}
          left={1}
        />
      </Box>
    </Box>
  );
};

export default memo(MessageGifViewer);
